import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useCompanyInfo, usePageContent } from '../lib/cms';

export default function KontaktPage() {
  const companyInfo = useCompanyInfo();
  const page = usePageContent('kontakt', {
    badge: "Kontakt & rådgivning",
    heading: "Kontakt Hovendals",
    text: "Overvejer du nyt hegn, carport eller låge – eller har du spørgsmål til et projekt? Udfyld formularen her, så vender vi hurtigt tilbage med svar eller et uforpligtende tilbud. Vi prioriterer personlig service og hjælper dig hele vejen fra idé til færdig løsning.",
    metaTitle: "Kontakt Hovendals » Få et uforpligtende tilbud her",
    metaDescription: "Tag kontakt til os, hvis du har brug for hjælp til opgaver som klejnsmed, byg og montage m.m. Så giver vi et uforpligtende tilbud.",
  });
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website_url: '', // Honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    if (formData.website_url) {
      setIsSubmitting(false);
      navigate('/tak-for-din-henvendelse');
      return;
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelse. Prøv venligst igen.');
      }

      navigate('/tak-for-din-henvendelse');
    } catch (err) {
      console.error('Webform submit error:', err);
      setErrorMessage(err.message || 'Der opstod en fejl. Ring direkte på 40 55 55 99.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={page.metaTitle || "Kontakt Hovendals » Få et uforpligtende tilbud her"}
        description={page.metaDescription || "Tag kontakt til os, hvis du har brug for hjælp til opgaver som klejnsmed, byg og montage m.m. Så giver vi et uforpligtende tilbud."}
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-headset" aria-hidden="true"></i>
              <span>{page.badge || "Kontakt & rådgivning"}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {page.heading || "Kontakt Hovendals"}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              {page.text || "Overvejer du nyt hegn, carport eller låge – eller har du spørgsmål til et projekt? Udfyld formularen her, så vender vi hurtigt tilbage med svar eller et uforpligtende tilbud. Vi prioriterer personlig service og hjælper dig hele vejen fra idé til færdig løsning."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Info Column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Personlig kontakt</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Vi er her for at hjælpe dig
                </h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  Skulle du have brug for en hurtig afklaring, er du naturligvis også velkommen til at kontakte os direkte på telefon – vi er her for at hjælpe dig!
                </p>
              </div>

              {/* Cards */}
              <div className="space-y-4">
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center shrink-0 text-xl">
                    <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Telefon</h3>
                    <p className="text-xs text-slate-500 mb-1">Mandag til fredag</p>
                    <a
                      href={`tel:${companyInfo.phoneFormatted}`}
                      className="text-lg font-bold text-[#004fa4] hover:text-[#183e7f] transition-colors"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center shrink-0 text-xl">
                    <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">E-mail</h3>
                    <p className="text-xs text-slate-500 mb-1">Vi svarer hurtigst muligt</p>
                    <a
                      href={`mailto:${companyInfo.email}?subject=Henvendelse%20fra%20hjemmeside`}
                      className="text-base font-bold text-[#004fa4] hover:text-[#183e7f] transition-colors break-all"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center shrink-0 text-xl">
                    <i className="fa-sharp fa-solid fa-location-dot" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Adresse & Firma</h3>
                    <p className="text-sm font-semibold text-slate-700 mt-0.5">
                      {companyInfo.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      {companyInfo.address}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      CVR: {companyInfo.cvr}
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                      <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="font-bold text-slate-900 text-sm">Følg os på Facebook</div>
                      <div className="text-xs text-slate-500">Se nye projekter og billeder</div>
                    </div>
                  </div>
                  <a
                    href={companyInfo.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-colors"
                  >
                    Besøg &rarr;
                  </a>
                </div>
              </div>
            </div>

            {/* Right Contact Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-slate-50 rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm">
                <div className="mb-8">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Send os en besked</span>
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                    Klar til at starte dit projekt?
                  </h3>
                  <p className="text-sm text-slate-600 mt-2">
                    Har du en forespørgsel eller brug for information? Send os en besked her, så følger vi op hurtigst muligt.
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-triangle-exclamation text-red-600 text-base mt-0.5" aria-hidden="true"></i>
                    <div>{errorMessage}</div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* MANDATORY Honeypot antispam */}
                  <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                    <label htmlFor="contact-website-url">Website URL (leave empty)</label>
                    <input
                      type="text"
                      id="contact-website-url"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.website_url}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit fulde navn"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#004fa4] focus:ring-2 focus:ring-[#004fa4]/20 text-slate-900 placeholder:text-slate-400 text-sm bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="F.eks. 40 55 55 99"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#004fa4] focus:ring-2 focus:ring-[#004fa4]/20 text-slate-900 placeholder:text-slate-400 text-sm bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        E-mailadresse <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder="din@email.dk"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#004fa4] focus:ring-2 focus:ring-[#004fa4]/20 text-slate-900 placeholder:text-slate-400 text-sm bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Emne / Produkt
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#004fa4] focus:ring-2 focus:ring-[#004fa4]/20 text-slate-900 text-sm bg-white"
                      >
                        <option value="">Vælg emne...</option>
                        <option value="Hegn">Hegn (Komposit, panel, smedejern mv.)</option>
                        <option value="Låger">Låger</option>
                        <option value="Porte">Porte (Skydeporte, foldeporte)</option>
                        <option value="Carport">Carport</option>
                        <option value="Skur">Skur</option>
                        <option value="Facader / Vinduespartier">Facader & Vinduespartier</option>
                        <option value="Andet">Andet</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Besked <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      placeholder="Skriv din besked her..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#004fa4] focus:ring-2 focus:ring-[#004fa4]/20 text-slate-900 placeholder:text-slate-400 text-sm bg-white resize-y"
                    ></textarea>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3.5 px-6 rounded-lg bg-[#004FA4] hover:bg-[#183E7F] active:bg-[#143463] text-white font-medium text-base shadow-sm hover:shadow transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fa-sharp fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-sharp fa-solid fa-paper-plane" aria-hidden="true"></i>
                          <span>Send besked</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Sektion */}
      <section className="w-full relative overflow-hidden">
        <div className="w-full h-[400px] md:h-[500px] lg:h-[550px] bg-slate-100 relative">
          <iframe
            title="Hovendals placering på Google Maps"
            src="https://maps.google.com/maps?q=Brandsbyvej+12,+5450+Otterup&t=&z=14&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(100%) invert(8%) contrast(95%)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full block"
          ></iframe>
        </div>
      </section>
    </>
  );
}
